import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_STORAGE_KEY = 'aravell_auth_user';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Save user to localStorage when it changes
  const saveUser = (userData) => {
    if (userData) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
    setUser(userData);
  };

  const checkAuth = useCallback(async () => {
    try {
      // First check localStorage for existing session
      const storedUser = localStorage.getItem(AUTH_STORAGE_KEY);
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setLoading(false);
        
        // Verify session is still valid in background
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            credentials: 'include'
          });
          if (!response.ok) {
            // Session expired, clear localStorage
            localStorage.removeItem(AUTH_STORAGE_KEY);
            setUser(null);
          }
        } catch (e) {
          // Network error, keep local session
        }
        return;
      }
      
      // No stored user, check with server
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        saveUser(userData);
      } else {
        saveUser(null);
      }
    } catch (err) {
      console.error('Auth check error:', err);
      // Check localStorage as fallback
      const storedUser = localStorage.getItem(AUTH_STORAGE_KEY);
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/auth-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = async (email, password) => {
    setError(null);
    
    // Use XMLHttpRequest to avoid postMessage cloning issues in Safari/iOS
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      // Don't use withCredentials - we store session in localStorage instead
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            // Save to localStorage for Safari/iOS compatibility
            saveUser(data);
            resolve(data);
          } else {
            const errorMsg = data.detail || 'Credencials incorrectes';
            setError(errorMsg);
            reject(new Error(errorMsg));
          }
        } catch (e) {
          const errorMsg = 'Error processant la resposta';
          setError(errorMsg);
          reject(new Error(errorMsg));
        }
      };
      
      xhr.onerror = function() {
        const errorMsg = 'Error de connexió. Comprova la teva connexió a internet.';
        setError(errorMsg);
        reject(new Error(errorMsg));
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  };

  const processGoogleSession = async (sessionId) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Authentication failed');
      }

      const userData = await response.json();
      saveUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear localStorage and state
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setUser(null);
    }
  };

  const isAdmin = user?.role === 'admin';
  const isEditor = user?.role === 'editor';
  const isAuthenticated = !!user;

  // Helper function for authenticated API calls
  const authFetch = useCallback(async (url, options = {}) => {
    const storedUser = localStorage.getItem(AUTH_STORAGE_KEY);
    let token = null;
    
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        token = userData.session_token;
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }
    
    const headers = {
      ...options.headers,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      isAuthenticated,
      isAdmin,
      isEditor,
      loginWithGoogle,
      loginWithPassword,
      processGoogleSession,
      logout,
      checkAuth,
      authFetch
    }}>
      {children}
    </AuthContext.Provider>
  );
};
